import { db } from './firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { createOrUpdateContact, addContactTag, updateContactCustomFields } from './activeCampaignService';

// Retry failed ActiveCampaign syncs
export const retryFailedActiveCampaignSyncs = async (shortcode) => {
  try {
    const userRef = doc(db, 'users', shortcode);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      console.error('User not found for retry:', shortcode);
      return;
    }
    
    const userData = userDoc.data();
    const pendingSyncs = userData.pendingActiveCampaignSync || [];
    
    if (pendingSyncs.length === 0) {
      return; // No pending syncs
    }
    
    console.log(`Retrying ${pendingSyncs.length} failed ActiveCampaign syncs for user:`, shortcode);
    
    const successfulSyncs = [];
    const failedSyncs = [];
    
    for (const sync of pendingSyncs) {
      try {
        if (sync.type === 'email_capture') {
          // Extract contact data (excluding customFields which is handled separately)
          const { customFields, ...contactData } = sync.data;
          
          // Retry contact creation/update
          const contactResult = await createOrUpdateContact(contactData);
          
          // Update user with new contact ID
          const contactId = contactResult.contactId || userData.activeCampaignId;
          if (contactId) {
            await updateDoc(userRef, {
              activeCampaignId: contactId
            });
            
            // Update custom fields if provided
            if (customFields) {
              try {
                await updateContactCustomFields(contactId, customFields);
              } catch (fieldsError) {
                console.warn('Failed to update custom fields during retry:', fieldsError);
                // Don't fail the entire sync if custom fields update fails
              }
            }
            
            // Add tag if specified (using tagName from contactData)
            if (contactData.tagName) {
              try {
                await addContactTag(contactId, null, contactData.tagName);
              } catch (tagError) {
                console.warn('Failed to add tag during retry:', tagError);
                // Don't fail the entire sync if tag addition fails
              }
            }
          }
          
          successfulSyncs.push(sync);
          console.log('Successfully retried email capture sync:', sync);
          
                 } else if (sync.type === 'metrics_update') {
           // Retry metrics update
           if (userData.activeCampaignId) {
             await updateContactCustomFields(userData.activeCampaignId, sync.data);
             successfulSyncs.push(sync);
             console.log('Successfully retried metrics update sync:', sync);
           } else {
             failedSyncs.push(sync);
             console.log('Cannot retry metrics update - no contact ID:', sync);
           }
         } else if (sync.type === 'phone_verification') {
           // Retry phone verification tag
           if (sync.data.contactId && sync.data.tagId) {
             await addContactTag(sync.data.contactId, sync.data.tagId);
             successfulSyncs.push(sync);
             console.log('Successfully retried phone verification tag sync:', sync);
           } else {
             failedSyncs.push(sync);
             console.log('Cannot retry phone verification tag - missing data:', sync);
           }
         } else if (sync.type === 'payment') {
           // Retry payment tag
           if (sync.data.contactId && sync.data.tagId) {
             await addContactTag(sync.data.contactId, sync.data.tagId);
             successfulSyncs.push(sync);
             console.log('Successfully retried payment tag sync:', sync);
           } else {
             failedSyncs.push(sync);
             console.log('Cannot retry payment tag - missing data:', sync);
           }
         }
      } catch (error) {
        console.error('Failed to retry sync:', sync, error);
        failedSyncs.push({
          ...sync,
          error: error.message,
          retryCount: (sync.retryCount || 0) + 1,
          lastRetry: Date.now()
        });
      }
    }
    
    // Update user with remaining failed syncs
    if (failedSyncs.length > 0) {
      await updateDoc(userRef, {
        pendingActiveCampaignSync: failedSyncs
      });
    } else {
      // Clear pending syncs if all successful
      await updateDoc(userRef, {
        pendingActiveCampaignSync: []
      });
    }
    
    console.log(`Retry completed. Successful: ${successfulSyncs.length}, Failed: ${failedSyncs.length}`);
    
    return {
      successful: successfulSyncs.length,
      failed: failedSyncs.length,
      total: pendingSyncs.length
    };
    
  } catch (error) {
    console.error('Error in retry service:', error);
    throw error;
  }
};

// Clean up old failed syncs (older than 24 hours)
export const cleanupOldFailedSyncs = async (shortcode) => {
  try {
    const userRef = doc(db, 'users', shortcode);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      return;
    }
    
    const userData = userDoc.data();
    const pendingSyncs = userData.pendingActiveCampaignSync || [];
    
    if (pendingSyncs.length === 0) {
      return;
    }
    
    const twentyFourHoursAgo = Date.now() - (24 * 60 * 60 * 1000);
    const validSyncs = pendingSyncs.filter(sync => {
      // Keep syncs that are less than 24 hours old and have been retried less than 3 times
      return sync.timestamp > twentyFourHoursAgo && (sync.retryCount || 0) < 3;
    });
    
    if (validSyncs.length !== pendingSyncs.length) {
      await updateDoc(userRef, {
        pendingActiveCampaignSync: validSyncs
      });
      
      console.log(`Cleaned up ${pendingSyncs.length - validSyncs.length} old failed syncs for user:`, shortcode);
    }
    
  } catch (error) {
    console.error('Error cleaning up old failed syncs:', error);
  }
};

// Get sync status for a user
export const getActiveCampaignSyncStatus = async (shortcode) => {
  try {
    const userRef = doc(db, 'users', shortcode);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      return null;
    }
    
    const userData = userDoc.data();
    const pendingSyncs = userData.pendingActiveCampaignSync || [];
    
    return {
      hasActiveCampaignId: !!userData.activeCampaignId,
      pendingSyncsCount: pendingSyncs.length,
      pendingSyncs: pendingSyncs,
      lastSync: userData.lastActiveCampaignSync
    };
    
  } catch (error) {
    console.error('Error getting sync status:', error);
    return null;
  }
};

export default {
  retryFailedActiveCampaignSyncs,
  cleanupOldFailedSyncs,
  getActiveCampaignSyncStatus
};
